/**
 * Space Invaders renderer (C). Draws an engine state onto a 224×256 2D context.
 * Sprites are bitmask rows ('#' = pixel) pre-reduced to horizontal runs so a
 * frame is a few hundred fillRect calls — cheap enough for phones and trivial
 * to test with a fake context. No text is drawn here; HUD and screens are DOM.
 */
import {
    WIDTH, HEIGHT, SHIELD_W, SHIELD_H,
} from './spaceInvadersEngine';

export const COLORS = {
    background: '#000000',
    sprite: '#33ff66',
    shield: '#33ff66',
    ground: '#33ff66',
    ufo: '#ff3355',
    shot: '#ffffff',
    bomb: '#ffffff',
};

export const SPRITES = {
    // index = invader type: 0 bottom rows (10 pts), 1 middle (20), 2 top (30)
    invader: [
        [
            [
                '....####....',
                '.##########.',
                '############',
                '###..##..###',
                '############',
                '...##..##...',
                '..##.##.##..',
                '##........##',
            ],
            [
                '....####....',
                '.##########.',
                '############',
                '###..##..###',
                '############',
                '..###..###..',
                '.##..##..##.',
                '..##....##..',
            ],
        ],
        [
            [
                '..#......#..',
                '...#....#...',
                '..########..',
                '.##.####.##.',
                '############',
                '#.########.#',
                '#.#......#.#',
                '...##..##...',
            ],
            [
                '..#......#..',
                '#..#....#..#',
                '#.########.#',
                '###.####.###',
                '############',
                '.##########.',
                '..#......#..',
                '.#........#.',
            ],
        ],
        [
            [
                '.....##.....',
                '....####....',
                '...######...',
                '..##.##.##..',
                '..########..',
                '....#..#....',
                '...#.##.#...',
                '..#.#..#.#..',
            ],
            [
                '.....##.....',
                '....####....',
                '...######...',
                '..##.##.##..',
                '..########..',
                '...#.##.#...',
                '..#......#..',
                '...#....#...',
            ],
        ],
    ],
    player: [
        '......#......',
        '.....###.....',
        '.....###.....',
        '.###########.',
        '#############',
        '#############',
        '#############',
        '#############',
    ],
    ufo: [
        '.....######.....',
        '...##########...',
        '..############..',
        '.##.##.##.##.##.',
        '################',
        '...###....###...',
        '....#......#....',
    ],
    explosion: [
        '.#....#..#..',
        '..#..#..#...',
        '...#....#...',
        '##..#..#..##',
        '...#....#...',
        '..#..#..#...',
        '.#..#.....#.',
        '#..........#',
    ],
};

export function spriteRuns(rows) {
    const runs = [];
    rows.forEach((row, y) => {
        let start = -1;
        for (let x = 0; x <= row.length; x += 1) {
            const on = x < row.length && row[x] === '#';
            if (on && start < 0) {
                start = x;
            }
            if (!on && start >= 0) {
                runs.push({ x: start, y, w: x - start });
                start = -1;
            }
        }
    });
    return runs;
}

// Derived once at module load; never mutated afterwards.
const RUNS = {
    invader: SPRITES.invader.map(frames => frames.map(spriteRuns)),
    player: spriteRuns(SPRITES.player),
    ufo: spriteRuns(SPRITES.ufo),
    explosion: spriteRuns(SPRITES.explosion),
};

function drawRuns(ctx, runs, x, y) {
    for (let i = 0; i < runs.length; i += 1) {
        const run = runs[i];
        ctx.fillRect(x + run.x, y + run.y, run.w, 1);
    }
}

function drawShield(ctx, shield) {
    for (let cy = 0; cy < SHIELD_H; cy += 1) {
        let start = -1;
        for (let cx = 0; cx <= SHIELD_W; cx += 1) {
            const on = cx < SHIELD_W && shield.cells[cy * SHIELD_W + cx] === 1;
            if (on && start < 0) {
                start = cx;
            }
            if (!on && start >= 0) {
                ctx.fillRect(shield.x + start, shield.y + cy, cx - start, 1);
                start = -1;
            }
        }
    }
}

export function render(ctx, state) {
    ctx.fillStyle = COLORS.background;
    ctx.fillRect(0, 0, WIDTH, HEIGHT);

    ctx.fillStyle = COLORS.ground;
    ctx.fillRect(0, HEIGHT - 8, WIDTH, 1);

    ctx.fillStyle = COLORS.shield;
    state.shields.forEach(shield => drawShield(ctx, shield));

    ctx.fillStyle = COLORS.sprite;
    const frame = state.animFrame ? 1 : 0;
    state.invaders.forEach(invader => {
        if (invader.alive) {
            drawRuns(ctx, RUNS.invader[invader.type][frame], Math.round(invader.x), invader.y);
        }
    });
    const playerRuns = state.player.respawnMs > 0 ? RUNS.explosion : RUNS.player;
    drawRuns(ctx, playerRuns, Math.round(state.player.x), state.player.y);

    if (state.ufo) {
        ctx.fillStyle = COLORS.ufo;
        drawRuns(ctx, RUNS.ufo, Math.round(state.ufo.x), state.ufo.y);
    }

    if (state.playerShot) {
        ctx.fillStyle = COLORS.shot;
        ctx.fillRect(state.playerShot.x, Math.round(state.playerShot.y), state.playerShot.w, state.playerShot.h);
    }

    ctx.fillStyle = COLORS.bomb;
    state.bombs.forEach(bomb => {
        ctx.fillRect(Math.round(bomb.x), Math.round(bomb.y), bomb.w, bomb.h);
    });
}
